"use client";

import type { JSX } from "react";
import { Icon, Image, Link, Text, View } from "@/components";
import type { cardUserProps } from "@/types";

/**
 * CardUser01
 *
 * @description
 * Organism-level component that renders a user profile card.
 * Displays the user's avatar, name, description, social media links
 * and action buttons such as follow and message.
 *
 * This component is intended to be used in dashboards, profile lists,
 * or social sections where user identity and actions are required.
 *
 * @component
 *
 * @param {cardUserProps} props - Component properties.
 * @param {string} props.name - Full name of the user.
 * @param {string} props.description - Short description or role of the user.
 * @param {boolean} props.follow - Indicates whether the current user is already following this profile.
 *
 * @returns {JSX.Element} A rendered user profile card component.
 *
 * @example
 * ```tsx
 * <CardUser01
 *   name="Juan Bianchi"
 *   description="Software Engineer"
 *   follow={true}
 * />
 * ```
 */
const CardUser01 = ({
  name,
  description,
  follow,
  image
}: cardUserProps): JSX.Element => (
  <View className="bg-white dark:bg-[#1e1e24] border border-gray-100 dark:border-gray-800 rounded-3xl p-6 sm:p-8 max-w-full w-full shadow-xl dark:shadow-2xl relative overflow-hidden transition-colors flex flex-col h-full">
    <View className="flex flex-col sm:flex-row gap-6 mb-6 items-center sm:items-start text-center sm:text-left flex-grow">
      <View className="relative shrink-0">
        <View className="p-1 rounded-full border-2 border-[#5865F2]/30 w-[100px] h-[100px] sm:w-[122px] sm:h-[122px]">
          <Image
            src={
              image ||
              "https://plus.unsplash.com/premium_photo-1689568126014-06fea9d5d341?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8cGVyZmlsfGVufDB8fDB8fHww"
            }
            alt={`${name} profile picture`}
            className="rounded-full w-full h-full object-cover"
            width={122}
            height={122}
            quality={90}
            loading="lazy"
            decoding="async"
          />
        </View>
        <View className="absolute bottom-2 right-1 bg-[#5865F2] text-white w-7 h-7 flex items-center justify-center rounded-full border-2 border-white dark:border-[#1e1e24]">
          <Icon name="check" className="text-xs" />
        </View>
      </View>
      <View className="flex flex-col pt-2 w-full">
        <Text
          type="h4"
          className="text-xl font-bold text-gray-900 dark:text-white mb-1 line-clamp-1"
        >
          {name}
        </Text>
        <Text className="text-gray-500 dark:text-gray-300 text-sm mb-4 line-clamp-2 min-h-[40px]">
          {description}
        </Text>
      </View>
    </View>
    <View className="border-t border-dashed border-gray-200 dark:border-gray-700/50 mb-6 mt-auto"></View>
    <View className="flex gap-3 w-full">
      <Link
        href="#"
        className="flex-1 py-2 px-2 rounded-full bg-[#5865F2] hover:bg-[#4752c4] text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors shadow-lg shadow-[#5865F2]/20 whitespace-nowrap"
      >
        <Icon
          name={follow ? "circle-plus" : "circle-check"}
          className="text-xs"
        />
        <Text>{follow ? "Seguir" : "Siguiendo"}</Text>
      </Link>
      <Link
        href="#"
        className="flex-1 py-2 px-2 rounded-full border border-[#5865F2] text-[#5865F2] hover:bg-[#5865F2]/10 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors whitespace-nowrap"
      >
        <Icon name="message" className="text-xs" />
        <Text>Mensaje</Text>
      </Link>
    </View>
  </View>
);

export default CardUser01;
